import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Loader2, X } from 'lucide-react';
import { toast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { approvalWorkflowService } from '../services/approval-workflow-service';
import {
  AVISO_SOPORTE_NO_PUEDE,
  atributosSoporteNoPuede,
  useEnSoporte,
} from '@/features/mini-prixia/soporte-solo-mira';
import type {
  CargoDisponible,
  LobObserverRow,
  WatcherUser,
} from '../types/approval-workflow.types';

/**
 * Observadores por línea de producto (F6 T14/T15).
 *
 * Un observador recibe un aviso de lo que pasa con las cotizaciones de su línea y puede abrirlas, pero ve
 * precios, no costos ni márgenes, y no firma. Se configura acá: una fila por línea, con personas o cargos.
 */

interface Props {
  /** Las líneas de producto (nivel 1) de la empresa. */
  lobs: Array<{ id: string; name: string }>;
}

/** Un observador mientras se está configurando: lleva el tipo adentro para no confundir ids. */
interface Elegido {
  clave: string;
  userId?: string;
  customRoleId?: string;
}

const clavePersona = (id: string) => `persona:${id}`;
const claveCargo = (id: string) => `cargo:${id}`;

export function LobObserversSection({ lobs }: Props) {
  const [personas, setPersonas] = useState<WatcherUser[]>([]);
  const [cargos, setCargos] = useState<CargoDisponible[]>([]);
  /** Lo elegido por línea, editable hasta que se guarda. */
  const [porLinea, setPorLinea] = useState<Record<string, Elegido[]>>({});
  const [guardando, setGuardando] = useState<string | null>(null);
  // Soporte sólo mira en Mi empresa: el servidor frena guardar observadores (SOPORTE_SOLO_MIRA).
  const enSoporte = useEnSoporte();

  useEffect(() => {
    let vigente = true;
    // Cada lectura falla por su cuenta: si no se pueden leer los cargos, se sigue configurando por persona.
    Promise.allSettled([
      approvalWorkflowService.getWatcherUsers(),
      approvalWorkflowService.getCargos(),
      approvalWorkflowService.getLobObservers(),
    ]).then(([p, c, o]) => {
      if (!vigente) return;
      setPersonas(p.status === 'fulfilled' && Array.isArray(p.value) ? p.value : []);
      setCargos(c.status === 'fulfilled' && Array.isArray(c.value) ? c.value : []);
      const filas: LobObserverRow[] =
        o.status === 'fulfilled' && Array.isArray(o.value) ? o.value : [];
      const agrupado: Record<string, Elegido[]> = {};
      for (const f of filas) {
        const lista = (agrupado[f.lobId] ??= []);
        if (f.userId) lista.push({ clave: clavePersona(f.userId), userId: f.userId });
        else if (f.customRoleId)
          lista.push({ clave: claveCargo(f.customRoleId), customRoleId: f.customRoleId });
      }
      setPorLinea(agrupado);
    });
    return () => {
      vigente = false;
    };
  }, []);

  const nombreDe = (e: Elegido) => {
    if (e.customRoleId) return cargos.find((c) => c.id === e.customRoleId)?.name ?? 'Cargo';
    const p = personas.find((x) => x.id === e.userId);
    return p ? `${p.firstName} ${p.lastName}` : 'Persona';
  };

  const agregar = (lobId: string, clave: string) => {
    if (!clave) return;
    const [tipo, id] = clave.split(':') as ['persona' | 'cargo', string];
    setPorLinea((prev) => {
      const actuales = prev[lobId] ?? [];
      if (actuales.some((e) => e.clave === clave)) return prev;
      const nuevo: Elegido = tipo === 'cargo' ? { clave, customRoleId: id } : { clave, userId: id };
      return { ...prev, [lobId]: [...actuales, nuevo] };
    });
  };

  const quitar = (lobId: string, clave: string) =>
    setPorLinea((prev) => ({
      ...prev,
      [lobId]: (prev[lobId] ?? []).filter((e) => e.clave !== clave),
    }));

  const guardar = async (lob: { id: string; name: string }) => {
    setGuardando(lob.id);
    try {
      await approvalWorkflowService.setLobObservers(
        lob.id,
        (porLinea[lob.id] ?? []).map((e) =>
          e.customRoleId ? { customRoleId: e.customRoleId } : { userId: e.userId! }
        )
      );
      toast({ title: `Observadores de ${lob.name} guardados` });
    } catch (err) {
      toast({
        title: 'Error',
        description: mensajeDeError(err, 'No se pudieron guardar los observadores'),
        variant: 'destructive',
      });
    } finally {
      setGuardando(null);
    }
  };

  return (
    <Card role="region" aria-label="Observadores por línea">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Eye className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Observadores por línea (ven precios, no costos; avisan, no firman)</CardTitle>
        </div>
        <CardDescription>
          Los observadores de una línea reciben un aviso de lo que pasa con sus cotizaciones y las
          pueden abrir, pero ven precios, no costos ni márgenes, y no firman. Se elige una persona o
          un cargo (lo reciben todas las personas que lo tienen).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {lobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            La empresa todavía no tiene líneas de producto.
          </p>
        ) : (
          <div className="divide-y rounded-md border">
            {lobs.map((lob) => {
              const elegidos = porLinea[lob.id] ?? [];
              const sinGente = elegidos.filter(
                (e) =>
                  e.customRoleId &&
                  (cargos.find((c) => c.id === e.customRoleId)?.userCount ?? 1) === 0
              );
              return (
                <div key={lob.id} className="space-y-2 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">{lob.name}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => guardar(lob)}
                      disabled={guardando === lob.id || enSoporte}
                      title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
                      aria-label={`Guardar observadores de ${lob.name}`}
                    >
                      {guardando === lob.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Guardar'
                      )}
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {elegidos.length === 0 && (
                      <span className="text-xs text-muted-foreground">Sin observadores</span>
                    )}
                    {elegidos.map((e) => (
                      <Badge key={e.clave} variant="secondary" className="gap-1">
                        {nombreDe(e)}
                        {e.customRoleId && <span className="text-[10px] opacity-70">cargo</span>}
                        <button
                          type="button"
                          aria-label={`Quitar ${nombreDe(e)} de ${lob.name}`}
                          onClick={() => quitar(lob.id, e.clave)}
                          {...atributosSoporteNoPuede(enSoporte)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <select
                    aria-label={`Agregar observador a ${lob.name}`}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value=""
                    onChange={(ev) => agregar(lob.id, ev.target.value)}
                    {...atributosSoporteNoPuede(enSoporte)}
                  >
                    <option value="">Agregar persona o cargo…</option>
                    {cargos.some((c) => !elegidos.some((e) => e.clave === claveCargo(c.id))) && (
                      <optgroup label="Por cargo">
                        {cargos
                          .filter((c) => !elegidos.some((e) => e.clave === claveCargo(c.id)))
                          .map((c) => (
                            <option key={c.id} value={claveCargo(c.id)}>
                              {c.name}
                            </option>
                          ))}
                      </optgroup>
                    )}
                    {personas.some(
                      (p) => !elegidos.some((e) => e.clave === clavePersona(p.id))
                    ) && (
                      <optgroup label="Por persona">
                        {personas
                          .filter((p) => !elegidos.some((e) => e.clave === clavePersona(p.id)))
                          .map((p) => (
                            <option key={p.id} value={clavePersona(p.id)}>
                              {p.firstName} {p.lastName}
                              {p.roleName ? ` · ${p.roleName}` : ''}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </select>
                  {sinGente.length > 0 && (
                    <p className="text-xs font-medium text-destructive">
                      Sin nadie asignado: {sinGente.map(nombreDe).join(', ')}. Nadie va a recibir
                      los avisos por ese cargo hasta que alguien lo tenga.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
